import { DonutChartWidget } from "../charts/DonutChartWidget";
import { LineChartWidget } from "../charts/LineChartWidget";
import { ReportSection } from "../ReportSection";
import { ReportTable, type TableColumn } from "../ReportTable";
import { StatCard } from "../StatCard";
import { fmtPercent } from "./formatters";

interface TasksData {
  byStatus: { open: number; inProgress: number; review: number; completed: number; cancelled: number };
  byPriority: { critical: number; high: number; medium: number; low: number };
  byProject: Array<{
    projectName: string;
    total: number;
    completed: number;
    open: number;
    overdue: number;
  }>;
  completionTrend: Array<{ week: string; completed: number; created: number }>;
  avgCompletionDays: number;
  summary: { total: number; completionRate: number; overdueRate: number };
}

interface ProjectRow {
  projectName: string;
  total: number;
  completed: number;
  open: number;
  overdue: number;
}

function rateColor(p: number): string {
  if (p > 70) return "hsl(142,72%,32%)";
  if (p >= 40) return "hsl(38,92%,30%)";
  return "hsl(0,72%,45%)";
}

export function TasksStatusReport({ data }: { data: TasksData }) {
  const donut = [
    { name: "Open", value: data.byStatus.open, color: "hsl(210,16%,60%)" },
    { name: "In Progress", value: data.byStatus.inProgress, color: "hsl(210,100%,50%)" },
    { name: "Review", value: data.byStatus.review, color: "hsl(270,75%,55%)" },
    { name: "Completed", value: data.byStatus.completed, color: "hsl(142,72%,40%)" },
    { name: "Cancelled", value: data.byStatus.cancelled, color: "hsl(0,72%,55%)" },
  ];
  const overdueCount = Math.round(
    (data.summary.total * data.summary.overdueRate) / 100,
  );

  const cols: Array<TableColumn<ProjectRow>> = [
    { key: "projectName", label: "Project" },
    { key: "total", label: "Total", align: "right" },
    { key: "completed", label: "Completed", align: "right" },
    { key: "open", label: "Open", align: "right" },
    {
      key: "overdue",
      label: "Overdue",
      align: "right",
      render: (r) =>
        r.overdue > 0 ? (
          <span className="text-[hsl(0,72%,45%)] font-medium">{r.overdue}</span>
        ) : (
          0
        ),
    },
    {
      key: "rate",
      label: "Rate",
      align: "right",
      render: (r) => {
        const rate = r.total > 0 ? (r.completed / r.total) * 100 : 0;
        return (
          <span style={{ color: rateColor(rate), fontWeight: 500 }}>
            {fmtPercent(rate)}
          </span>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <StatCard label="Total" value={data.summary.total} />
        <StatCard label="Open" value={data.byStatus.open} />
        <StatCard label="In Progress" value={data.byStatus.inProgress} />
        <StatCard label="Completed" value={data.byStatus.completed} tone="good" />
        <StatCard label="Overdue" value={overdueCount} tone="bad" />
        <StatCard
          label="Completion Rate"
          value={fmtPercent(data.summary.completionRate)}
          tone={data.summary.completionRate > 70 ? "good" : "warn"}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ReportSection title="Status Breakdown">
          <DonutChartWidget data={donut} />
        </ReportSection>
        <ReportSection title="Weekly Trend">
          <LineChartWidget
            data={data.completionTrend}
            xKey="week"
            lines={[
              { key: "created", name: "Created", color: "hsl(210,100%,50%)" },
              { key: "completed", name: "Completed", color: "hsl(142,72%,40%)" },
            ]}
          />
        </ReportSection>
      </div>

      <ReportSection
        title="By Project"
        subtitle={`Avg completion: ${data.avgCompletionDays} days`}
      >
        <ReportTable
          columns={cols}
          rows={data.byProject}
          rowKey={(r, i) => `${r.projectName}-${i}`}
        />
      </ReportSection>
    </div>
  );
}
